import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { LogEntry, UseLogsOptions, UseLogsReturn } from "../types";
import { useLogsSettings } from "./useLogsSettings";
import { useLogsQuery } from "./useLogsQuery";
import { useLogsWebSocket } from "./useLogsWebSocket";
import { extractCommonLabels, hasAnyUniqueLabels } from "../utils/labelProcessor";
import { downloadLogs as downloadLogsUtil, copyLogsToClipboard } from "../utils/downloadLogs";
import { setQueryLabel, removeQueryLabel, getQueryLabel } from "../utils/queryParser";
import { resolveExpression } from "../../datepicker";

const MAX_LOGS = 1000;

/**
 * Main hook for logs functionality
 * Orchestrates settings, queries, WebSocket, and state management
 */
export function useLogs(options: UseLogsOptions): UseLogsReturn {
  const {
    org,
    defaultQuery,
    storageKey,
    fileNamePrefix,
    logsEndpoint,
    grafanaEndpoint,
    beforeQuery,
    timezone,
  } = options;

  // Settings hook - load first to get saved query
  const { settings, updateSettings } = useLogsSettings({ storageKey });

  // Core state - initialize from saved settings if available
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [query, setQueryInternal] = useState(() => settings.savedQuery ?? defaultQuery);
  const [from, setFromInternal] = useState<string | null>(() => settings.savedFrom ?? "now-1h");
  const [to, setToInternal] = useState<string | null>(() => settings.savedTo ?? "now");
  const [isLive, setIsLive] = useState(false);
  const [isLiveDirty, setIsLiveDirty] = useState(false);
  const [hasExecuted, setHasExecuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track query at live start for dirty detection
  const liveQueryRef = useRef<string | null>(null);
  const liveFromRef = useRef<string | null>(null);

  // Live pause buffering
  const bufferedEntriesRef = useRef<LogEntry[]>([]);
  const isLivePausedRef = useRef(false);
  const [bufferedCount, setBufferedCount] = useState(0);

  // Wrapped setters that also persist to localStorage
  const setQuery = useCallback(
    (q: string | ((prev: string) => string)) => {
      setQueryInternal((prev) => {
        const newQuery = typeof q === "function" ? q(prev) : q;
        updateSettings({ savedQuery: newQuery || undefined });
        return newQuery;
      });
    },
    [updateSettings]
  );

  const setFrom = useCallback(
    (f: string | null) => {
      setFromInternal(f);
      updateSettings({ savedFrom: f ?? undefined });
    },
    [updateSettings]
  );

  const setTo = useCallback(
    (t: string | null) => {
      setToInternal(t);
      updateSettings({ savedTo: t ?? undefined });
    },
    [updateSettings]
  );

  // Reset query to defaults
  const resetQuery = useCallback(() => {
    setQueryInternal(defaultQuery);
    setFromInternal("now-1h");
    setToInternal("now");
    updateSettings({
      savedQuery: undefined,
      savedFrom: undefined,
      savedTo: undefined,
    });
  }, [defaultQuery, updateSettings]);

  // Query hook
  const {
    execute: executeQueryInternal,
    cancel: cancelQueryInternal,
    isLoading,
    error: queryError,
  } = useLogsQuery({ org, logsEndpoint, timezone });

  // Clear the live pause buffer
  const clearBuffer = useCallback(() => {
    bufferedEntriesRef.current = [];
    isLivePausedRef.current = false;
    setBufferedCount(0);
  }, []);

  // Handle incoming live logs
  const handleLiveMessage = useCallback((entries: LogEntry[]) => {
    if (isLivePausedRef.current) {
      // Buffer entries while paused
      bufferedEntriesRef.current = [...entries, ...bufferedEntriesRef.current].slice(0, MAX_LOGS);
      setBufferedCount(bufferedEntriesRef.current.length);
    } else {
      // Normal behavior - prepend to visible entries
      setLogEntries((prev) => {
        const combined = [...entries, ...prev];
        return combined.slice(0, MAX_LOGS);
      });
    }
  }, []);

  // Set live paused state (for scroll-based buffering)
  const setLivePaused = useCallback((paused: boolean) => {
    isLivePausedRef.current = paused;
    if (!paused) {
      // Flush buffer into visible entries
      const buffered = bufferedEntriesRef.current;
      if (buffered.length > 0) {
        setLogEntries((prev) => {
          const combined = [...buffered, ...prev];
          return combined.slice(0, MAX_LOGS);
        });
      }
      bufferedEntriesRef.current = [];
      setBufferedCount(0);
    }
  }, []);

  // Handle WebSocket error
  const handleWebSocketError = useCallback(() => {
    // Flush any buffered entries before reporting error
    const buffered = bufferedEntriesRef.current;
    if (buffered.length > 0) {
      setLogEntries((prev) => {
        const combined = [...buffered, ...prev];
        return combined.slice(0, MAX_LOGS);
      });
    }
    clearBuffer();
    setError("Live connection error. Please try again.");
    setIsLive(false);
  }, [clearBuffer]);

  // WebSocket hook
  const {
    connect: wsConnect,
    disconnect: wsDisconnect,
    status: liveStatus,
  } = useLogsWebSocket({
    org,
    logsEndpoint,
    onMessage: handleLiveMessage,
    onError: handleWebSocketError,
  });

  // Sorted logs based on orderBy setting
  const logs = useMemo(() => {
    const sorted = [...logEntries].sort((a, b) => {
      return settings.orderBy === "desc"
        ? b.timestamp - a.timestamp
        : a.timestamp - b.timestamp;
    });
    return sorted;
  }, [logEntries, settings.orderBy]);

  // Common labels across all entries
  const commonLabels = useMemo(() => {
    return extractCommonLabels(logEntries);
  }, [logEntries]);

  // Whether any entries have unique labels
  const hasUniqueLabels = useMemo(() => {
    return hasAnyUniqueLabels(logEntries);
  }, [logEntries]);

  // Execute query
  const executeQuery = useCallback(async () => {
    if (beforeQuery && !beforeQuery()) {
      return;
    }

    if (!from) {
      setError("Please select a start time");
      return;
    }

    setError(null);
    setHasExecuted(true);

    try {
      const entries = await executeQueryInternal(query, from, to || "now");
      // null means cancelled — preserve existing results
      if (entries !== null) {
        setLogEntries(entries);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "An error occurred");
    }
  }, [beforeQuery, from, to, query, executeQueryInternal]);

  // Cancel running query
  const cancelQuery = useCallback(() => {
    cancelQueryInternal();
  }, [cancelQueryInternal]);

  // Start live mode
  const startLive = useCallback(() => {
    if (beforeQuery && !beforeQuery()) {
      return;
    }

    if (!from) {
      setError("Please select a start time");
      return;
    }

    setError(null);
    setHasExecuted(true);
    setLogEntries([]);
    clearBuffer();
    setIsLive(true);
    setIsLiveDirty(false);
    liveQueryRef.current = query;
    liveFromRef.current = from;

    // Convert from time to nanoseconds for WebSocket
    let startNs: string | undefined;
    if (from && from !== "now") {
      const fromDate = resolveExpression(from, timezone);
      if (fromDate) {
        startNs = String(fromDate.getTime()) + "000000";
      }
    }
    wsConnect(query, startNs);
  }, [beforeQuery, from, query, wsConnect, timezone, clearBuffer]);

  // Stop live mode
  const stopLive = useCallback(() => {
    // Flush any buffered entries before stopping
    const buffered = bufferedEntriesRef.current;
    if (buffered.length > 0) {
      setLogEntries((prev) => {
        const combined = [...buffered, ...prev];
        return combined.slice(0, MAX_LOGS);
      });
    }
    clearBuffer();
    wsDisconnect();
    setIsLive(false);
    setIsLiveDirty(false);
    liveQueryRef.current = null;
    liveFromRef.current = null;
  }, [wsDisconnect, clearBuffer]);

  // Restart live mode - no timeout needed since cleanup is now synchronous
  const restartLive = useCallback(() => {
    wsDisconnect();
    // Reset state and start fresh
    setLogEntries([]);
    clearBuffer();
    setIsLiveDirty(false);
    liveQueryRef.current = query;
    liveFromRef.current = from;
    setIsLive(true);

    // Convert from time to nanoseconds for WebSocket
    let startNs: string | undefined;
    if (from && from !== "now") {
      const fromDate = resolveExpression(from, timezone);
      if (fromDate) {
        startNs = String(fromDate.getTime()) + "000000";
      }
    }
    wsConnect(query, startNs);
  }, [wsDisconnect, wsConnect, query, from, timezone, clearBuffer]);

  // Detect dirty state during live mode
  useEffect(() => {
    if (isLive && liveQueryRef.current !== null) {
      const isDirty =
        query !== liveQueryRef.current || from !== liveFromRef.current;
      setIsLiveDirty(isDirty);
    }
  }, [isLive, query, from]);

  // Flush buffer when orderBy changes during live pause
  useEffect(() => {
    if (isLivePausedRef.current && bufferedEntriesRef.current.length > 0) {
      setLivePaused(false);
    }
  }, [settings.orderBy, setLivePaused]);

  // Download logs
  const downloadLogsHandler = useCallback(
    (format: "json" | "txt") => {
      downloadLogsUtil(logs, format, commonLabels, settings.orderBy, fileNamePrefix);
    },
    [logs, commonLabels, settings.orderBy, fileNamePrefix]
  );

  // Copy logs to clipboard
  const copyLogsHandler = useCallback(async () => {
    await copyLogsToClipboard(logs, commonLabels, settings.orderBy);
  }, [logs, commonLabels, settings.orderBy]);

  // Query manipulation helpers
  const setQueryKeyValue = useCallback(
    (key: string, value: string, remove?: boolean) => {
      if (remove || !value) {
        setQuery((q) => removeQueryLabel(q, key));
      } else {
        setQuery((q) => setQueryLabel(q, key, value));
      }
    },
    []
  );

  const getQueryKeyValue = useCallback(
    (key: string): string | null => {
      return getQueryLabel(query, key);
    },
    [query]
  );

  // Build Grafana explore redirect URL (for form submission)
  const grafanaRedirectUrl = useMemo(() => {
    // Build range object - use relative time format if possible
    let range: { from: string; to: string };

    // Check if 'from' is a relative expression (e.g., "now-1h")
    const isRelativeFrom = from && from.startsWith("now");
    const isRelativeTo = !to || to === "now" || to === "";

    if (isRelativeFrom && isRelativeTo) {
      // Use relative format directly
      range = { from: from, to: "now" };
    } else {
      // Convert to milliseconds timestamp
      const fromMs = from ? resolveExpression(from, timezone)?.getTime().toString() ?? "now-1h" : "now-1h";
      const toMs = to ? resolveExpression(to, timezone)?.getTime().toString() ?? "now" : "now";
      range = { from: fromMs, to: toMs };
    }

    const left = encodeURIComponent(
      JSON.stringify({
        queries: [
          {
            expr: query,
            datasource: "logs",
            refId: "A",
          },
        ],
        range,
      }),
    );

    return `/explore?left=${left}`;
  }, [query, from, to, timezone]);

  // Open in Grafana - now just a placeholder, actual opening is done via form in LogsPage
  const openInGrafana = useCallback(() => {
    // This is a no-op now - the actual opening is handled by the form submission in LogsPage
    // Kept for interface compatibility
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsDisconnect();
    };
  }, [wsDisconnect]);

  return {
    // Data
    logs,
    commonLabels,
    hasUniqueLabels,

    // Query state
    query,
    setQuery,
    from,
    to,
    setFrom,
    setTo,

    // Loading states
    isLoading,
    isLive,
    isLiveDirty,
    liveStatus,
    hasExecuted,

    // Actions
    executeQuery,
    cancelQuery,
    startLive,
    stopLive,
    restartLive,
    downloadLogs: downloadLogsHandler,
    copyLogs: copyLogsHandler,
    resetQuery,

    // Settings
    settings,
    updateSettings,

    // Query helpers
    setQueryKeyValue,
    getQueryKeyValue,

    // Grafana
    openInGrafana,
    grafanaRedirectUrl,

    // Error
    error: error || (queryError?.message ?? null),

    // Live pause
    bufferedCount,
    setLivePaused,
  };
}
